import type { WbsTargetFrequency } from '../../../../api/wbsTargetApis';
import {
  countForLookAheadPeriod,
  type LookAheadPeriod,
  type TargetsLookAheadConfig,
} from './targetsLookAheadConfig';

export type GanttViewMode = 'schedule' | 'targets';

export interface TargetsPeriodColumn {
  /** Stable cell key, e.g. day:2026-07-31 */
  key: string;
  frequency: WbsTargetFrequency;
  /** API targetPeriod value */
  targetPeriod: string;
  /** Short header label */
  label: string;
  /** Sub-label under header (optional) */
  subLabel?: string;
  /** Inclusive period start (local midnight) */
  start: Date;
  /** Inclusive period end (local end of day) */
  end: Date;
  /** True when the period ends before today (local) */
  isPast: boolean;
  /**
   * Day scale only: weekend / holiday from project workingCalender.
   * Applied after columns are built (see applyWorkingCalendarToColumns).
   */
  isNonWorking?: boolean;
}

export interface TargetsLookAheadWindow {
  start: Date;
  end: Date;
  columns: TargetsPeriodColumn[];
  frequency: WbsTargetFrequency;
  /** Export cap only. The filter uses start/end. */
  maxDaysFromToday: number;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

/** ISO week: Monday = start. */
function startOfIsoWeek(d: Date): Date {
  const day = startOfLocalDay(d);
  const dow = day.getDay(); // 0 Sun … 6 Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(day, offset);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Portal format: YYYY-Www (ISO week). */
export function formatIsoWeekPeriod(d: Date): string {
  const weekStart = startOfIsoWeek(d);
  // ISO week-year: Thursday of this week determines the year
  const thursday = addDays(weekStart, 3);
  const year = thursday.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const week1Start = startOfIsoWeek(jan4);
  const week =
    Math.floor((startOfLocalDay(weekStart).getTime() - week1Start.getTime()) / (7 * 86400000)) + 1;
  return `${year}-W${pad2(week)}`;
}

function formatMonthPeriod(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function monthLabel(d: Date): string {
  return d.toLocaleString(undefined, { month: 'short' });
}

function dayLabel(d: Date): string {
  return String(d.getDate());
}

/** Compact ISO-week range for header, e.g. "31 Jul – 6 Aug" or "3–9 Aug". */
function weekRangeLabel(weekStart: Date, weekEnd: Date): string {
  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const startMon = monthLabel(weekStart);
  const endMon = monthLabel(weekEnd);
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startDay}–${endDay} ${startMon}`;
  }
  return `${startDay} ${startMon} – ${endDay} ${endMon}`;
}

/**
 * Build the look-ahead window for the selected Period.
 * - day: N calendar days from today
 * - week: N ISO weeks from the current week
 * - month: N calendar months from the current month
 */
export function buildTargetsLookAheadWindow(
  period: LookAheadPeriod,
  config: TargetsLookAheadConfig,
  nowInput: Date = new Date(),
): TargetsLookAheadWindow {
  const today = startOfLocalDay(nowInput);
  const dayCount = countForLookAheadPeriod(config, 'day');
  const weekCount = countForLookAheadPeriod(config, 'week');
  const monthCount = countForLookAheadPeriod(config, 'month');

  if (period === 'day') {
    const start = today;
    const end = endOfLocalDay(addDays(today, dayCount - 1));
    const columns: TargetsPeriodColumn[] = [];
    for (let i = 0; i < dayCount; i += 1) {
      const day = addDays(today, i);
      const ymd = formatYmd(day);
      columns.push({
        key: `day:${ymd}`,
        frequency: 'DAY',
        targetPeriod: ymd,
        label: dayLabel(day),
        subLabel: monthLabel(day),
        start: startOfLocalDay(day),
        end: endOfLocalDay(day),
        isPast: endOfLocalDay(day).getTime() < today.getTime(),
      });
    }
    return { start, end, columns, frequency: 'DAY', maxDaysFromToday: config.maxDaysFromToday };
  }

  if (period === 'week') {
    const start = startOfIsoWeek(today);
    const columns: TargetsPeriodColumn[] = [];
    for (let i = 0; i < weekCount; i += 1) {
      const weekStart = addDays(start, i * 7);
      const weekEnd = endOfLocalDay(addDays(weekStart, 6));
      const targetPeriod = formatIsoWeekPeriod(weekStart);
      columns.push({
        key: `week:${targetPeriod}`,
        frequency: 'WEEK',
        targetPeriod,
        label: targetPeriod.replace(/^\d{4}-W/, 'W'),
        subLabel: weekRangeLabel(weekStart, weekEnd),
        start: weekStart,
        end: weekEnd,
        isPast: weekEnd.getTime() < today.getTime(),
      });
    }
    const end = columns[columns.length - 1]?.end ?? endOfLocalDay(today);
    return { start, end, columns, frequency: 'WEEK', maxDaysFromToday: config.maxDaysFromToday };
  }

  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const columns: TargetsPeriodColumn[] = [];
  for (let i = 0; i < monthCount; i += 1) {
    const monthStart = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const monthEnd = endOfLocalDay(new Date(today.getFullYear(), today.getMonth() + i + 1, 0));
    const targetPeriod = formatMonthPeriod(monthStart);
    columns.push({
      key: `month:${targetPeriod}`,
      frequency: 'MONTH',
      targetPeriod,
      label: monthLabel(monthStart),
      subLabel: String(monthStart.getFullYear()),
      start: monthStart,
      end: monthEnd,
      isPast: monthEnd.getTime() < today.getTime(),
    });
  }
  const end = columns[columns.length - 1]?.end ?? endOfLocalDay(today);
  return { start, end, columns, frequency: 'MONTH', maxDaysFromToday: config.maxDaysFromToday };
}

/**
 * Cap the Excel/PDF end date. The on-screen filter keeps the full period window.
 */
export function capLookAheadExportEndDate(
  windowEnd: Date,
  maxDaysFromToday: number,
  nowInput: Date = new Date(),
): Date {
  if (!Number.isFinite(maxDaysFromToday) || maxDaysFromToday <= 0) return windowEnd;
  const today = startOfLocalDay(nowInput);
  const maxEnd = endOfLocalDay(addDays(today, maxDaysFromToday));
  return windowEnd.getTime() > maxEnd.getTime() ? maxEnd : windowEnd;
}

function formatShortLocalDate(d: Date): string {
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Caption suffix for look-ahead Excel/PDF export — date range and period duration.
 */
export function formatLookAheadExportWindowCaption(
  windowStart: Date,
  windowEnd: Date,
  period: LookAheadPeriod,
  config: TargetsLookAheadConfig,
  nowInput: Date = new Date(),
): string {
  const exportEnd = capLookAheadExportEndDate(windowEnd, config.maxDaysFromToday, nowInput);
  const range = `${formatShortLocalDate(windowStart)} – ${formatShortLocalDate(exportEnd)}`;
  const wasCapped = exportEnd.getTime() !== endOfLocalDay(windowEnd).getTime();
  const count = countForLookAheadPeriod(config, period);
  const unit = period === 'day' ? 'day' : period === 'week' ? 'week' : 'month';
  const duration = wasCapped
    ? `${config.maxDaysFromToday} days`
    : `${count} ${unit}${count === 1 ? '' : 's'}`;

  return `${range} · ${duration}`;
}

/** Header band height — match Gantt scale_height (60). */
export const TARGETS_HEADER_HEIGHT = 60;

/**
 * Match DHTMLX grid Duration column width (see GanttPage columns config).
 * Day P | A each get this width.
 */
export const TARGETS_DAY_SUBCOLUMN_WIDTH = 80;

/**
 * Min width per period column (day / week / month).
 * Each period is one parent column with equal P | A sub-columns.
 */
export const TARGETS_CELL_MIN_WIDTH: Record<WbsTargetFrequency, number> = {
  /** 2 × Duration-width sub-columns (P | A) */
  DAY: TARGETS_DAY_SUBCOLUMN_WIDTH * 2,
  /** Wider so week range + "Planned" / "Achieved" sub-headers fit */
  WEEK: 128,
  MONTH: 120,
};

/** Shared grid template for P | A sub-columns inside a period. */
export const TARGETS_PERIOD_SUBCOLUMNS = '1fr 1fr';
